import * as React from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  BulkAssemblyMethod,
  DEFAULT_GIBSON_PARAMETERS,
  DisplayFragmentProductionMethod,
  FragmentProductionMethod,
  GibsonFragmentProductionMethod,
  GibsonParameters,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {AssemblyParametersControlsLayout} from 'molbio/bulk-assembly/setup-modal/controls/AssemblyParametersControlsLayout';
import {FragmentProductionMethodDropdown} from 'molbio/bulk-assembly/setup-modal/controls/FragmentProductionMethodDropdown';
import {HomologyRegionMaxLength} from 'molbio/bulk-assembly/setup-modal/controls/HomologyRegionMaxLength';
import {HomologyRegionMinLength} from 'molbio/bulk-assembly/setup-modal/controls/HomologyRegionMinLength';
import {HomologyRegionMinMeltingTemp} from 'molbio/bulk-assembly/setup-modal/controls/HomologyRegionMinMeltingTemp';
import {PrimerMinMeltingTemp} from 'molbio/bulk-assembly/setup-modal/controls/PrimerMinMeltingTemp';
import {PrimerPairMaxMeltingTempDifference} from 'molbio/bulk-assembly/setup-modal/controls/PrimerPairMaxMeltingTempDifference';
import {ResetToDefaultsButton} from 'molbio/bulk-assembly/setup-modal/controls/ResetToDefaultsButton';
import {ReviewParametersText} from 'molbio/bulk-assembly/setup-modal/controls/ReviewParametersText';

export function areGibsonParametersValid(gibsonParameters: GibsonParameters): boolean {
  return (
    [
      FragmentProductionMethod.PRIMER_PAIR,
      FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS,
      FragmentProductionMethod.EXISTING_CUT_SITES,
    ].includes(gibsonParameters.defaultFragmentProductionMethod) &&
    gibsonParameters.bindingRegionMinMeltingTemp != null &&
    gibsonParameters.primerMinMeltingTemp != null &&
    gibsonParameters.bindingAndHomologyRegionMinLength != null &&
    gibsonParameters.bindingAndHomologyRegionMaxLength != null &&
    gibsonParameters.primerPairMaxMeltingTempDifference != null
  );
}

export interface GibsonParametersControlsProps {
  gibsonParameters: GibsonParameters;
  patchGibsonParameters: (newParams: Partial<GibsonParameters>) => void;
  fragmentProductionMethodOverride?: DisplayFragmentProductionMethod;
  isReadOnly: boolean;
}

/**
 * A set of inputs to set parameters for a Gibson assembly.
 *
 * @param props Component props
 * @param {GibsonParameters} props.gibsonParameters The parameters that can be updated with this set of inputs.
 * @param {(number | null)} props.gibsonParameters.bindingRegionMinMeltingTemp The minimum melting temperature, in degrees Celsius, of the homology region.
 * @param {(number | null)} props.gibsonParameters.primerMinMeltingTemp The minimum melting temperature, in degrees Celsius, of any primers used in the assembly.
 * @param {(number | null)} props.gibsonParameters.bindingAndHomologyRegionMinLength The minimum length, in base pairs, of the homology region.
 * @param {(number | null)} props.gibsonParameters.bindingAndHomologyRegionMaxLength The maximum length, in base pairs, of the homology region.
 * @param {(number | null)} props.gibsonParameters.primerPairMaxMeltingTempDifference The maximum difference, in degrees Celsius, between the melting temperatures of all primers used in this assembly.
 * @param {boolean} props.gibsonParameters.reuseExistingPrimers Whether to look for existing matching primers before generating new primers for this assembly.
 * @param {function} props.patchGibsonParameters A function that takes a subset of Gibson parameters and updates state.
 * @param {(DisplayFragmentProductionMethod | undefined)} props.fragmentProductionMethodOverride An override for the fragment production method; locks the dropdown as well.
 * @param {boolean} props.isReadOnly Whether the modal should be read-only; passed when assembly has been finalized
 * @constructor
 */
export function GibsonParametersControls(props: GibsonParametersControlsProps): JSX.Element {
  const fragmentProductionMethod =
    props.fragmentProductionMethodOverride ?? props.gibsonParameters.defaultFragmentProductionMethod;
  const homologyRegionMinMeltingTemp = (
    <HomologyRegionMinMeltingTemp
      homologyRegionMinMeltingTemp={props.gibsonParameters.bindingRegionMinMeltingTemp}
      updateHomologyRegionMinMeltingTemp={(bindingRegionMinMeltingTemp) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
          cloningMethod: BulkAssemblyMethod.GIBSON,
          parameterName: 'bindingRegionMinMeltingTemp',
          parameterValue: bindingRegionMinMeltingTemp,
        });
        props.patchGibsonParameters({bindingRegionMinMeltingTemp});
      }}
      isDisabled={props.isReadOnly}
    />
  );
  const homologyRegionMinLength = (
    <HomologyRegionMinLength
      homologyRegionMinLength={props.gibsonParameters.bindingAndHomologyRegionMinLength}
      updateHomologyRegionMinLength={(bindingAndHomologyRegionMinLength) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
          cloningMethod: BulkAssemblyMethod.GIBSON,
          parameterName: 'bindingAndHomologyRegionMinLength',
          parameterValue: bindingAndHomologyRegionMinLength,
        });
        props.patchGibsonParameters({bindingAndHomologyRegionMinLength});
      }}
      isDisabled={props.isReadOnly}
    />
  );
  const homologyRegionMaxLength = (
    <HomologyRegionMaxLength
      homologyRegionMaxLength={props.gibsonParameters.bindingAndHomologyRegionMaxLength}
      updateHomologyRegionMaxLength={(bindingAndHomologyRegionMaxLength) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
          cloningMethod: BulkAssemblyMethod.GIBSON,
          parameterName: 'bindingAndHomologyRegionMaxLength',
          parameterValue: bindingAndHomologyRegionMaxLength,
        });
        props.patchGibsonParameters({bindingAndHomologyRegionMaxLength});
      }}
      isDisabled={props.isReadOnly}
    />
  );

  const primerMinMeltingTemp = (
    <PrimerMinMeltingTemp
      primerMinMeltingTemp={props.gibsonParameters.primerMinMeltingTemp}
      updatePrimerMinMeltingTemp={(primerMinMeltingTemp) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
          cloningMethod: BulkAssemblyMethod.GIBSON,
          parameterName: 'primerMinMeltingTemp',
          parameterValue: primerMinMeltingTemp,
        });
        props.patchGibsonParameters({primerMinMeltingTemp});
      }}
      isDisabled={props.isReadOnly}
    />
  );

  const primerPairMaxMeltingTempDifference = (
    <PrimerPairMaxMeltingTempDifference
      primerPairMaxMeltingTempDifference={props.gibsonParameters.primerPairMaxMeltingTempDifference}
      updatePrimerPairMaxMeltingTempDifference={(primerPairMaxMeltingTempDifference) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
          cloningMethod: BulkAssemblyMethod.GIBSON,
          parameterName: 'primerPairMaxMeltingTempDifference',
          parameterValue: primerPairMaxMeltingTempDifference,
        });
        props.patchGibsonParameters({primerPairMaxMeltingTempDifference});
      }}
      isDisabled={props.isReadOnly}
    />
  );

  const fragmentProductionMethodDropdown = (
    <FragmentProductionMethodDropdown
      fragmentProductionMethod={fragmentProductionMethod}
      updateFragmentProductionMethod={(fragmentProductionMethod) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_FRAGMENT_PRODUCTION_METHOD, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
          cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
          fragmentProductionMethod,
        });
        props.patchGibsonParameters({defaultFragmentProductionMethod: fragmentProductionMethod});
      }}
      bulkAssemblyMethod={BulkAssemblyMethod.GIBSON}
      dropdownMethods={[
        FragmentProductionMethod.PRIMER_PAIR,
        FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS,
        ...([FragmentProductionMethod.EXISTING_CUT_SITES] as Array<GibsonFragmentProductionMethod>),
      ]}
      disabled={props.fragmentProductionMethodOverride != null || props.isReadOnly}
    />
  );

  return (
    <AssemblyParametersControlsLayout
      controls={[
        <ReviewParametersText />,
        !props.isReadOnly && (
          <ResetToDefaultsButton
            resetOnClick={() => {
              props.patchGibsonParameters(DEFAULT_GIBSON_PARAMETERS);
            }}
          />
        ),
        fragmentProductionMethodDropdown,
        <div />, // spacer, forces previous control to occupy only one column
        ...(fragmentProductionMethod === FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS ||
        fragmentProductionMethod === FragmentProductionMethod.EXISTING_CUT_SITES
          ? [homologyRegionMinLength, homologyRegionMaxLength, homologyRegionMinMeltingTemp]
          : [
              homologyRegionMinMeltingTemp,
              primerMinMeltingTemp,
              homologyRegionMinLength,
              homologyRegionMaxLength,
              primerPairMaxMeltingTempDifference,
            ]),
        <div />, // spacer, forces previous control to occupy only one column
      ]}
    />
  );
}
